'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  Alert01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
  Notification01Icon,
  SearchIcon,
  Tick02Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import Link from 'next/link'
import { SettingsConnectionPanel } from '@/components/settings/SettingsConnectionPanel'
import { SettingsAuthGateModal } from '@/components/settings/SettingsAuthGateModal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getChannelIcon } from '@/lib/budget/channel-icons'
import { subchannelFaviconUrl } from '@/lib/connectors/subchannel-favicon'
import { FALLBACK_MARKETPLACE_CHANNELS } from '@/lib/marketplace/fallback-data'
import { cn } from '@/lib/utils'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface SubchannelView {
  key: string
  channel_key: string
  name: string
  logo_text: string | null
  logo_url: string | null
  description: string | null
  default_auth_mode: 'atrium_managed' | 'user_oauth' | 'either'
  status: 'coming_soon' | 'available' | 'deprecated'
  display_order: number | null
  homepage_url: string | null
  connection: null | {
    id: string
    auth_mode: 'atrium_managed' | 'user_oauth'
    status: 'connected' | 'disconnected' | 'error' | 'pending'
    monthly_budget_cents: number
    connected_at: string | null
    last_synced_at: string | null
    display_account_name: string | null
  }
}

export interface ChannelView {
  key: string
  name: string
  accent_hex: string
  display_order: number
  description: string | null
  subchannels: SubchannelView[]
}

type ChannelRow = {
  channel_key: string
  channel_name: string
  channel_display_order: number
  channel_accent_hex: string
  subchannel: SubchannelView
}

type ChannelGroup = {
  channel_key: string
  channel_name: string
  channel_display_order: number
  channel_accent_hex: string
  rows: ChannelRow[]
}

type FilterKey = 'all' | 'enabled' | 'disabled' | 'error'
type CategoryKey = 'all' | 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'retail'
type PillStatus = 'enabled' | 'disabled' | 'error' | 'coming_soon'

const CATEGORY_OPTIONS: Array<{ value: CategoryKey; label: string }> = [
  { value: 'all', label: 'All categories' },
  { value: 'search', label: 'Search' },
  { value: 'social', label: 'Social' },
  { value: 'tv', label: 'TV' },
  { value: 'programmatic', label: 'Programmatic' },
  { value: 'audio', label: 'Audio' },
  { value: 'retail', label: 'Retail' },
]

/**
 * Adapt the marketplace fallback catalog (used logged-out / on a cold preview
 * DB) into the ChannelView shape this page renders. Lets logged-out visitors
 * browse the full channel list — selecting one opens the sign-up gate.
 */
function fallbackChannelViews(): ChannelView[] {
  return FALLBACK_MARKETPLACE_CHANNELS.map(channel => ({
    key: channel.key,
    name: channel.name,
    accent_hex: channel.accent_hex ?? '#0A8A6E',
    display_order: channel.display_order ?? 0,
    description: channel.description ?? null,
    subchannels: channel.subchannels.map(sub => ({
      key: sub.key,
      channel_key: channel.key,
      name: sub.name,
      logo_text: null,
      logo_url: null,
      description: sub.description ?? null,
      default_auth_mode: 'either' as const,
      status: 'available' as const,
      display_order: sub.display_order ?? null,
      homepage_url: null,
      connection: null,
    })),
  }))
}

function applyDemoOverrides(channels: ChannelView[]): ChannelView[] {
  return channels.map(channel => ({
    ...channel,
    subchannels: channel.subchannels.map(sub => {
      if (sub.key === 'sonobi') {
        return { ...sub, name: 'Atrium Performance Network', status: 'available' as const }
      }
      return { ...sub, status: 'coming_soon' as const }
    }),
  }))
}

function buildGroups(channels: ChannelView[]): ChannelGroup[] {
  return channels
    .map(channel => {
      const rows = channel.subchannels
        .filter(subchannel => subchannel.status !== 'deprecated')
        .map(subchannel => ({
          channel_key: channel.key,
          channel_name: channel.name,
          channel_display_order: channel.display_order,
          channel_accent_hex: channel.accent_hex,
          subchannel,
        }))
        .sort((a, b) => {
          const oa = a.subchannel.display_order ?? Number.MAX_SAFE_INTEGER
          const ob = b.subchannel.display_order ?? Number.MAX_SAFE_INTEGER
          if (oa !== ob) return oa - ob
          return a.subchannel.name.localeCompare(b.subchannel.name)
        })
      return {
        channel_key: channel.key,
        channel_name: channel.name,
        channel_display_order: channel.display_order,
        channel_accent_hex: channel.accent_hex,
        rows,
      }
    })
    .filter(group => group.rows.length > 0)
    .sort((a, b) => {
      if (a.channel_display_order !== b.channel_display_order) {
        return a.channel_display_order - b.channel_display_order
      }
      return a.channel_name.localeCompare(b.channel_name)
    })
}

function pillStatusFor(sub: SubchannelView): PillStatus {
  if (sub.status === 'coming_soon') return 'coming_soon'
  const status = sub.connection?.status
  if (status === 'error') return 'error'
  if (status === 'connected' || status === 'pending') return 'enabled'
  return 'disabled'
}

function matchesCategory(group: ChannelGroup, category: CategoryKey): boolean {
  if (category === 'all') return true
  const normalizedName = group.channel_name.toLowerCase()
  return group.channel_key === category || normalizedName === category
}

function matchesSearch(row: ChannelRow, query: string): boolean {
  if (!query) return true
  const haystack = `${row.subchannel.name} ${row.subchannel.description ?? ''} ${row.channel_name}`.toLowerCase()
  return haystack.includes(query)
}

function matchesStatus(row: ChannelRow, filter: FilterKey): boolean {
  if (filter === 'all') return true
  return pillStatusFor(row.subchannel) === filter
}

export default function SettingsPage() {
  const [channels, setChannels] = useState<ChannelView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loggedOut, setLoggedOut] = useState(false)
  const [activeRow, setActiveRow] = useState<ChannelRow | null>(null)
  const [gateRow, setGateRow] = useState<ChannelRow | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [category, setCategory] = useState<CategoryKey>('all')

  const fetchCatalog = useCallback(async (signal?: { cancelled: boolean }) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/connectors/catalog')
      // Anonymous visitors get a 401 — fall back to the public catalog so the
      // page is browsable logged-out (selecting a channel opens the sign-up gate).
      if (res.status === 401) {
        if (!signal?.cancelled) {
          setLoggedOut(true)
          setChannels(applyDemoOverrides(fallbackChannelViews()))
        }
        return
      }
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`)
      if (!signal?.cancelled) {
        setLoggedOut(false)
        const next: ChannelView[] = data.channels ?? []
        setChannels(applyDemoOverrides(next))
      }
    } catch (err) {
      if (!signal?.cancelled) setError((err as Error).message)
    } finally {
      if (!signal?.cancelled) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const signal = { cancelled: false }
    fetchCatalog(signal)
    return () => {
      signal.cancelled = true
    }
  }, [fetchCatalog])

  // Logged-out: selecting a channel prompts sign-up. Logged-in: open the panel.
  const handleOpenRow = useCallback(
    (row: ChannelRow) => {
      if (loggedOut) setGateRow(row)
      else setActiveRow(row)
    },
    [loggedOut],
  )

  const groups = useMemo(() => buildGroups(channels), [channels])
  const rowCount = groups.reduce((count, group) => count + group.rows.length, 0)
  const normalizedQuery = query.trim().toLowerCase()

  const counts = useMemo(() => {
    const next = { all: 0, enabled: 0, disabled: 0, error: 0 }
    for (const group of groups) {
      if (!matchesCategory(group, category)) continue
      for (const row of group.rows) {
        if (!matchesSearch(row, normalizedQuery)) continue
        next.all += 1
        const ps = pillStatusFor(row.subchannel)
        if (ps !== 'coming_soon') next[ps] += 1
      }
    }
    return next
  }, [groups, normalizedQuery, category])

  const filteredGroups = useMemo(
    () =>
      groups
        .filter(group => matchesCategory(group, category))
        .map(group => ({
          ...group,
          rows: group.rows.filter(
            row => matchesSearch(row, normalizedQuery) && matchesStatus(row, filter),
          ),
        }))
        .filter(group => group.rows.length > 0),
    [groups, normalizedQuery, filter, category],
  )
  const filteredRowCount = filteredGroups.reduce((count, group) => count + group.rows.length, 0)

  return (
    <div className="min-h-full bg-canvas text-text-strong">
      <LobbyHeaderMeta>
        {rowCount} channel{rowCount === 1 ? '' : 's'} · {counts.enabled} enabled
      </LobbyHeaderMeta>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-8">
        <div className="pb-16 pt-6">
          {/* Title */}
          <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-text-strong sm:text-[34px]">
            Channel settings
          </h1>
          <p className="mt-1 max-w-[520px] text-[13.5px] leading-[1.5] text-text">
            {loggedOut
              ? 'Browse every channel Atrium can run. Sign in to connect one.'
              : 'Connect or disconnect every channel your workspace can use.'}
          </p>

          {!loggedOut && (
            <Link
              href="/settings/notifications"
              className="mt-5 flex items-center justify-between rounded-card border-[0.5px] border-line bg-surface px-4 py-3 text-text-strong transition-colors hover:border-brand/40"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-atrium-background text-brand">
                  <HugeiconsIcon icon={Notification01Icon} className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-semibold">Notifications</span>
                  <span className="block truncate text-[12.5px] text-text-muted">
                    Alert channels, digest cadence, and quiet hours
                  </span>
                </span>
              </span>
              <HugeiconsIcon icon={ArrowRight01Icon} className="size-4 shrink-0 text-text-muted" />
            </Link>
          )}

          {loading && (
            <div className="mt-6 rounded-card border-[0.5px] border-line bg-surface px-5 py-10 text-center text-sm text-text-muted">
              Loading channels…
            </div>
          )}

          {error && (
            <div className="mt-6 rounded-card border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-600">
              Couldn&apos;t load channels: {error}
            </div>
          )}

          {!loading && !error && rowCount === 0 && (
            <div className="mt-6 rounded-card border-[0.5px] border-line bg-surface px-5 py-10 text-center text-sm text-text-muted">
              No channels available yet.
            </div>
          )}

          {!loading && !error && rowCount > 0 && (
            <div className="mt-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <FilterTabs value={filter} onChange={setFilter} counts={counts} />
                <div className="flex w-full items-center gap-2.5 sm:ml-auto sm:w-auto">
                  <SearchInput query={query} onChange={setQuery} />
                  <CategorySelect value={category} onChange={setCategory} />
                </div>
              </div>

              {filteredRowCount === 0 ? (
                <FilteredEmptyState
                  onClearSearch={() => setQuery('')}
                  onResetFilters={() => {
                    setFilter('all')
                    setCategory('all')
                  }}
                />
              ) : (
                <div className="mt-4 space-y-6">
                  {filteredGroups.map(group => (
                    <section key={group.channel_key}>
                      <div
                        className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.12em]"
                        style={{ color: group.channel_accent_hex }}
                      >
                        {group.channel_name}
                      </div>
                      <div className="overflow-hidden rounded-card border-[0.5px] border-line bg-surface">
                        <div className="divide-y divide-line-subtle">
                          {group.rows.map(row => (
                            <SettingsRow key={row.subchannel.key} row={row} onOpen={handleOpenRow} />
                          ))}
                        </div>
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SettingsConnectionPanel
        subchannel={activeRow?.subchannel ?? null}
        channelAccentHex={activeRow?.channel_accent_hex ?? ''}
        channelName={activeRow?.channel_name ?? ''}
        onClose={() => setActiveRow(null)}
        onChanged={() => fetchCatalog()}
      />

      <SettingsAuthGateModal
        open={gateRow !== null}
        channelName={gateRow?.subchannel.name ?? null}
        channelAccentHex={gateRow?.channel_accent_hex ?? null}
        onClose={() => setGateRow(null)}
      />
    </div>
  )
}

function FilterTabs({
  value,
  onChange,
  counts,
}: {
  value: FilterKey
  onChange: (v: FilterKey) => void
  counts: Record<FilterKey, number>
}) {
  const tabs: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'enabled', label: 'Enabled', count: counts.enabled },
    { key: 'disabled', label: 'Disabled', count: counts.disabled },
    { key: 'error', label: 'Error', count: counts.error },
  ]

  return (
    <div
      role="tablist"
      aria-label="Filter channels"
      className="inline-flex items-center gap-1 self-start rounded-[12px] border-[0.5px] border-line bg-surface p-1"
    >
      {tabs.map(t => {
        const active = value === t.key
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
              active ? 'bg-brand text-white' : 'text-text-muted hover:text-text-strong',
            )}
          >
            <span>{t.label}</span>
            <span
              className={cn(
                'font-mono text-[10.5px] tabular-nums',
                active ? 'text-white/70' : 'text-text-muted-3',
              )}
            >
              {t.count}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function SearchInput({
  query,
  onChange,
}: {
  query: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-card border-[0.5px] border-line bg-surface px-3.5 py-2 focus-within:border-brand sm:w-[300px] sm:flex-none">
      <HugeiconsIcon icon={SearchIcon} className="size-4 shrink-0 text-text-muted" />
      <input
        type="search"
        placeholder="Search channels…"
        value={query}
        onChange={e => onChange(e.target.value)}
        className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-text-strong outline-none placeholder:text-text-muted"
      />
      {query && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="text-text-muted hover:text-text-strong"
        >
          <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
        </button>
      )}
    </div>
  )
}

function CategorySelect({
  value,
  onChange,
}: {
  value: CategoryKey
  onChange: (value: CategoryKey) => void
}) {
  return (
    <Select value={value} onValueChange={value => onChange(value as CategoryKey)}>
      <SelectTrigger className="h-[40px] w-40 shrink-0 rounded-card border-[0.5px] border-line bg-surface px-3 text-[13.5px] text-text-strong shadow-none">
        <SelectValue placeholder="All categories" />
      </SelectTrigger>
      <SelectContent>
        {CATEGORY_OPTIONS.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function FilteredEmptyState({
  onClearSearch,
  onResetFilters,
}: {
  onClearSearch: () => void
  onResetFilters: () => void
}) {
  return (
    <div className="mt-4 rounded-card border-[0.5px] border-line bg-surface py-12 text-center text-sm text-text-muted">
      <p>No channels match your filters.</p>
      <div className="mt-2 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={onClearSearch}
          className="font-semibold text-brand hover:text-brand-deep"
        >
          Clear search
        </button>
        <span aria-hidden="true" className="text-text-muted-3">·</span>
        <button
          type="button"
          onClick={onResetFilters}
          className="font-semibold text-brand hover:text-brand-deep"
        >
          Reset filters
        </button>
      </div>
    </div>
  )
}

function StatusPill({ status }: { status: PillStatus }) {
  if (status === 'enabled') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-pill bg-brand px-2.5 py-1 text-[11px] font-bold text-white">
        <HugeiconsIcon icon={Tick02Icon} className="size-3" />
        Enabled
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-pill bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-600">
        <HugeiconsIcon icon={Alert01Icon} className="size-3" />
        Error
      </span>
    )
  }
  if (status === 'coming_soon') {
    return (
      <span className="inline-flex items-center rounded-pill border-[0.5px] border-line bg-surface px-2.5 py-1 text-[11px] font-semibold text-text-muted">
        Coming soon
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-pill border-[0.5px] border-line bg-surface px-2.5 py-1 text-[11px] font-semibold text-text-muted">
      Disabled
    </span>
  )
}

/**
 * Subchannel brand logo. Renders the resolved favicon on a white chip ringed
 * in the channel accent; on a missing/failed favicon it falls back to the
 * channel-accent tile with the channel icon (or the subchannel's initials),
 * so every row stays colorful. Plain <img> — identical behavior on mobile.
 */
function SubchannelLogo({ row }: { row: ChannelRow }) {
  const sub = row.subchannel
  const ChannelIcon = getChannelIcon(row.channel_key)
  const faviconUrl = subchannelFaviconUrl(sub.key, sub.logo_url)
  const [failed, setFailed] = useState(false)

  if (faviconUrl && !failed) {
    return (
      <div
        className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-[11px] bg-white shadow-v3-tile ring-2"
        style={{ ['--tw-ring-color' as string]: row.channel_accent_hex }}
      >
        <img
          src={faviconUrl}
          alt=""
          width={22}
          height={22}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="size-[22px] object-contain"
        />
      </div>
    )
  }

  return (
    <div
      className="grid size-10 shrink-0 place-items-center rounded-[11px] text-white shadow-v3-tile"
      style={{ background: row.channel_accent_hex }}
    >
      {ChannelIcon ? (
        <HugeiconsIcon icon={ChannelIcon} className="size-5" />
      ) : (
        <span className="font-mono text-[13px] font-semibold">
          {sub.logo_text || sub.name[0]}
        </span>
      )}
    </div>
  )
}

function SettingsRow({ row, onOpen }: { row: ChannelRow; onOpen: (row: ChannelRow) => void }) {
  const sub = row.subchannel
  const comingSoon = sub.status === 'coming_soon'

  return (
    <button
      type="button"
      onClick={() => { if (!comingSoon) onOpen(row) }}
      disabled={comingSoon}
      className={cn(
        'group flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition-colors focus:outline-none',
        comingSoon ? 'cursor-not-allowed opacity-60' : 'hover:bg-canvas focus-visible:bg-canvas',
      )}
    >
      <SubchannelLogo row={row} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-bold text-text-strong">{sub.name}</div>
        {sub.description && (
          <div className="truncate text-[12px] text-text-muted-2">{sub.description}</div>
        )}
      </div>
      <StatusPill status={pillStatusFor(sub)} />
      {!comingSoon && (
        <HugeiconsIcon
          icon={ArrowRight01Icon}
          className="size-4 shrink-0 text-text-muted-3 opacity-60 transition-opacity group-hover:opacity-100"
        />
      )}
    </button>
  )
}
